import { EMPTY_VALUE } from '@outpoll/utils';
import Decimal from 'decimal.js';
import { Numeric } from '@/lib/types/common';
/**
 * Format a prefix volume into a compact string with k, m, b suffixes.
 *
 * formatAmount(17000000) => "$17m"
 * formatAmount(1234567)  => "$1.2m"
 * formatAmount(100000)   => "$100k"
 * formatAmount(950)      => "$950"
 * formatAmount(-2500000) => "-$2.5m"
 */
export function formatAmount(value: number, prefix: string = '$', suffix: string = ''): string {
  const abs = Math.abs(value);

  // helper: round to 1 decimal if < 10, else no decimals, and strip trailing .0
  function fmt(num: number): string {
    const absNum = Math.abs(num);
    const rounded = absNum >= 10 ? Math.round(num) : Math.round(num * 10) / 10;
    return rounded.toString().replace(/\.0$/, '');
  }

  let amountSuffix: string;
  let scaled: number;

  if (abs >= 1e9) {
    amountSuffix = 'b';
    scaled = value / 1e9;
  } else if (abs >= 1e6) {
    amountSuffix = 'm';
    scaled = value / 1e6;
  } else if (abs >= 1e3) {
    amountSuffix = 'k';
    scaled = value / 1e3;
  } else {
    // under 1k: just return the raw number
    return `${value < 0 ? '-' : ''}${prefix}${Math.abs(value)}${suffix}`;
  }

  const sign = scaled < 0 ? '-' : '';
  return `${sign}${prefix}${fmt(scaled)}${amountSuffix}${suffix}`;
}

/** The two precisions a coin carries: `dps` for the everyday look, `dpf` for what
 *  it can actually hold. */
type CoinPrecision = { decimalPlacesShort: number; decimalPlacesFull: number };

/** Marks an amount too small to show at the given precision: "> 0.001". */
const BELOW_MARKER = '> ';

/**
 * A money amount at a fixed number of decimals: three for dollars and stables,
 * a coin's own `dps` (decimalPlacesShort) when one is at hand — pass it in.
 * A non-zero amount too small for that precision reads as the threshold
 * ("> 0.001"), never as "0.000". `prefix` goes on the number, inside the
 * threshold marker, which is why the `$` belongs here and not at the call site.
 *
 * formatMoney(1234.5678)                 => "1,234.568"
 * formatMoney(12, { prefix: '$' })       => "$12.000"
 * formatMoney(0.0004)                    => "> 0.001"
 * formatMoney(-0.0004, { prefix: '$' })  => "> -$0.001"
 * formatMoney(0.62, { decimals: 2 })     => "0.62"
 * formatMoney(0.00012, { decimals: 5 })  => "0.00012"
 * formatMoney(12, { signed: true })      => "+12.000"
 *
 * Pass `coin` for a coin-denominated figure: it reads at the coin's `dps` and keeps
 * whatever the value carries beyond it, up to the coin's `dpf` — so a swap receipt
 * neither pads "12.500000" nor cuts "0.00012345" down to five places.
 *
 * `prefix` and `signed` live inside the marker ("> +$0.001") — composing them at
 * the call site would produce "+> $0.001". `plain` drops the grouping and the
 * marker for strings that are parsed back (inline edit fields).
 */
export function formatMoney(
  value: number,
  {
    coin,
    decimals = coin?.decimalPlacesShort ?? 3,
    maxDecimals = coin?.decimalPlacesFull ?? decimals,
    prefix = '',
    signed = false,
    plain = false,
  }: {
    coin?: CoinPrecision;
    decimals?: number;
    maxDecimals?: number;
    prefix?: string;
    signed?: boolean;
    plain?: boolean;
  } = {},
): string {
  const max = Math.max(decimals, maxDecimals);
  const sign = value < 0 ? '-' : signed && value > 0 ? '+' : '';
  const safe = Number.isFinite(value) ? value : 0;
  // `value === 0` also catches -0, which would otherwise print as "-0.000".
  const abs = Math.abs(safe === 0 ? 0 : safe);

  // Machine-readable: no grouping and no marker, because the string goes back into
  // an input and is parsed again ("1,234.50" would `parseFloat` down to 1).
  if (plain) return `${sign}${prefix}${abs.toFixed(decimals)}`;

  if (isBelowMoneyThreshold(safe, max)) {
    return `${BELOW_MARKER}${sign}${prefix}${(10 ** -max).toFixed(max)}`;
  }

  return `${sign}${prefix}${abs.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: max,
  })}`;
}

/** Share prices are quoted in cents, at most one decimal: "27", "41.6". */
export function formatCentsValue(cents: Numeric): string {
  return new Decimal(cents).toDecimalPlaces(1).toString();
}

/** {@link formatCentsValue} with the sign: "27¢", "41.6¢". */
export function formatCents(cents: Numeric): string {
  return `${formatCentsValue(cents)}¢`;
}

/** Non-zero, but smaller than `decimals` can show — render the threshold instead. */
export function isBelowMoneyThreshold(value: number, decimals: number = 3): boolean {
  if (decimals <= 0) return false;
  return Number.isFinite(value) && value !== 0 && Math.abs(value) < 10 ** -decimals;
}

/** What a balance figure reads while the user keeps balances hidden. */
export const HIDDEN_BALANCE = '••••';

/** `format(value)`, masked while balances are hidden and a dash until the value exists. */
export function formatMasked<T>(
  value: T | null | undefined,
  hidden: boolean,
  format: (value: T) => string,
): string {
  if (hidden) return HIDDEN_BALANCE;
  if (value == null) return EMPTY_VALUE;
  return format(value);
}

/** {@link formatMoney} through {@link formatMasked}. */
export function formatMaskedMoney(
  value: number | null | undefined,
  hidden: boolean,
  options?: Parameters<typeof formatMoney>[1],
): string {
  return formatMasked(value, hidden, (v: number) => formatMoney(v, options));
}
